import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight, ShieldCheck, Globe2, Wallet, Clock, Star, Quote, ChevronDown } from "lucide-react";
import axios from "axios";
import SEOHead from "@/components/site/SEOHead";
import FAQSection from "@/components/site/FAQSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- SEO -------------------------------------------------------------------
const CANONICAL = "https://emgroupdigital.fr/site-location";
const TITLE = "Création site web La Réunion — EM Group | À partir de 180€/mois";
const DESCRIPTION =
  "Agence digitale basée à La Réunion. Site web professionnel livré en 15 à 30 jours, sans gros investissement de départ. Devis gratuit sous 24h.";
const KEYWORDS =
  "création site web La Réunion, agence web La Réunion, site web professionnel Réunion, agence digitale Réunion, site internet 974, créer site web île de la Réunion, EM Group";

const LOCAL_BUSINESS = {
  name: "EM Group",
  description:
    "Agence digitale basée à La Réunion, spécialisée dans la création de sites web professionnels en location mensuelle, le branding et l'accompagnement digital des TPE/PME.",
  email: "contact@emgroupdigital.fr",
  telephone: "+262 262 66 79 70",
  url: CANONICAL,
  image: "https://emgroupdigital.fr/brand/logo-em-group.png",
  areaServed: ["La Réunion", "974", "France"],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Saint-Denis",
    addressRegion: "La Réunion",
    postalCode: "97400",
    addressCountry: "RE",
  },
};

const SERVICE_SCHEMA = {
  name: "Création de site web professionnel à La Réunion",
  serviceType: "Création et location de site internet",
  description:
    "Agence digitale à La Réunion : création complète de site web professionnel livrée en 15 à 30 jours. Design sur-mesure, hébergement sécurisé, nom de domaine et maintenance inclus à partir de 180€/mois.",
  areaServed: "La Réunion",
  price: "180",
  priceCurrency: "EUR",
  priceDescription: "À partir de 180€/mois TTC — création, hébergement et nom de domaine inclus.",
};

const FAQ_ITEMS = [
  {
    q: "Combien coûte un site web professionnel à La Réunion ?",
    a: "À partir de 180€/mois en formule location, sans investissement initial de 3 000 €. L'abonnement inclut la création complète, l'hébergement, le nom de domaine et la maintenance. Un site sur-mesure 100% adapté à votre activité, pour un budget mensuel maîtrisé.",
  },
  {
    q: "Qu'est-ce qui est inclus dans l'offre location de site ?",
    a: "Création complète sur-mesure (design pro + contenus rédigés), nom de domaine, hébergement sécurisé, certificat SSL, formulaire de contact, design responsive (mobile / tablette / desktop), maintenance et mises à jour techniques. Vous démarrez sans avance, on gère tout, vous validez.",
  },
  {
    q: "Quel est l'engagement pour la formule Location ?",
    a: "La formule Location est souscrite pour une durée minimale de 12 mois calendaires à compter de la mise en ligne du site, à 180€/mois TTC, incluant création, hébergement, nom de domaine, maintenance et support. Passée cette période minimum de 12 mois, l'abonnement est résiliable à tout moment avec un préavis de 30 jours calendaires, par email à contact@emgroupdigital.fr. Cet engagement garantit une prestation pérenne et permet de démarrer sans investissement initial de 3 000 €.",
  },
];

// --- Conversion tracking ---------------------------------------------------
const GOOGLE_ADS_CONVERSION_ID = "AW-18201979242";
const GOOGLE_ADS_LEAD_LABEL = "kUtFCJzRrrccEOrSsOdD";

function fireLeadConversion(value = 30) {
  if (
    GOOGLE_ADS_CONVERSION_ID &&
    GOOGLE_ADS_LEAD_LABEL &&
    typeof window !== "undefined" &&
    window.gtag
  ) {
    window.gtag("event", "conversion", {
      send_to: `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_LEAD_LABEL}`,
      value,
      currency: "EUR",
    });
  }
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "Lead", {
      content_name: "Site Location 180€/mois",
      value,
      currency: "EUR",
    });
  }
}

const TESTIMONIALS = [
  {
    company: "Solmis Habitat",
    logo: "/clients/solmis-habitat.png",
    initials: "SH",
    quote: "Site moderne et professionnel. Nous recommandons vivement EM Group.",
    rating: 5,
  },
  {
    company: "Innocéan",
    logo: "/clients/innocean.png",
    initials: "I",
    quote: "Résultat fluide et parfaitement adapté à La Réunion. Je recommande EM Group.",
    rating: 5,
  },
];

function LogoOrFallback({ src, alt, initials }) {
  const [failed, setFailed] = useState(false);
  if (failed || !src) {
    return (
      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-white to-white/85 text-[#0A0A0E] font-outfit font-semibold text-base flex items-center justify-center shadow-lg shadow-black/30">
        {initials}
      </div>
    );
  }
  return (
    <div className="w-12 h-12 rounded-2xl bg-white p-1.5 flex items-center justify-center shadow-lg shadow-black/30 ring-1 ring-white/10">
      <img
        src={src}
        alt={alt}
        width="48"
        height="48"
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        className="max-w-full max-h-full object-contain"
      />
    </div>
  );
}

function TestimonialCard({ company, logo, initials, quote, rating, index }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: 0.08 * index }}
      data-testid={`testimonial-${index}`}
      className="group relative rounded-3xl border border-white/[0.08] bg-gradient-to-br from-white/[0.04] via-white/[0.02] to-transparent backdrop-blur-xl p-6 md:p-7 hover:border-white/15 hover:-translate-y-0.5 transition-all duration-300"
    >
      {/* Watermark quote */}
      <Quote
        size={56}
        className="absolute top-4 right-5 text-[#E52431]/15 -scale-x-100"
        strokeWidth={1.5}
        aria-hidden="true"
      />

      {/* Stars */}
      <div className="flex items-center gap-1 mb-4" aria-label={`${rating} étoiles sur 5`}>
        {Array.from({ length: rating }).map((_, i) => (
          <Star key={i} size={14} className="fill-amber-400 text-amber-400" />
        ))}
      </div>

      {/* Quote */}
      <blockquote className="text-[15px] md:text-base text-white/85 leading-relaxed font-light relative z-10">
        <span className="text-[#E52431] font-serif text-2xl leading-none mr-1 align-top">“</span>
        {quote}
        <span className="text-[#E52431] font-serif text-2xl leading-none ml-1 align-top">”</span>
      </blockquote>

      {/* Footer / attribution */}
      <figcaption className="mt-5 pt-5 border-t border-white/5 flex items-center gap-3">
        <LogoOrFallback src={logo} alt={company} initials={initials} />
        <div>
          <div className="text-sm font-medium text-white">{company}</div>
          <div className="text-[11px] uppercase tracking-[0.12em] text-white/40">Client EM Group</div>
        </div>
      </figcaption>
    </motion.figure>
  );
}

const ARGUMENTS = [
  {
    Icon: Wallet,
    title: "Sans investissement initial",
    text: "Pas de 3 000 € à sortir. Vous démarrez avec un budget mensuel, sans surprise.",
  },
  {
    Icon: Globe2,
    title: "Création complète incluse",
    text: "Site sur-mesure, design pro, contenus rédigés. On gère tout, vous validez.",
  },
  {
    Icon: ShieldCheck,
    title: "Hébergement + nom de domaine inclus",
    text: "Domaine, hébergement sécurisé, maintenance, mises à jour : 100 % inclus.",
  },
  {
    Icon: Clock,
    title: "Réponse sous 24h",
    text: "On vous rappelle dans la journée pour cadrer votre projet, sans engagement.",
  },
];

// --- CGV — Conditions Générales de Vente de la formule Location ---------
const CGV_ARTICLES = [
  { n: 1, title: "Objet", body: "Les présentes Conditions Générales de Vente (CGV) régissent la formule Location de site web proposée par EM Group Digital (« EM Group ») aux entreprises basées à La Réunion (974) et en France métropolitaine.\n\nL'offre concerne la création, l'hébergement et la maintenance d'un site internet professionnel." },
  { n: 2, title: "Formule Location", body: "La formule Location inclut :\n\n• Création d'un site web professionnel sur-mesure jusqu'à 5 pages en base\n• Nom de domaine (.fr, .re ou .com — première année incluse)\n• Hébergement professionnel sécurisé\n• Certificat SSL et design responsive (mobile / tablette / desktop)\n• Maintenance technique et mises à jour de sécurité\n• Rédaction des contenus de base\n• Support par email\n\nLes prestations complémentaires (pages additionnelles, refonte majeure, e-commerce, contenus rédactionnels étendus) font l'objet d'un devis séparé." },
  { n: 3, title: "Tarifs et facturation", body: "Le tarif de la formule Location démarre à 180€/mois TTC. Un devis personnalisé est établi lors du premier échange en fonction du nombre de pages, du volume de contenus et des fonctionnalités spécifiques demandées.\n\nLes factures sont émises mensuellement et payables à réception. Les tarifs indiqués s'entendent toutes taxes comprises (TVA applicable au taux en vigueur)." },
  { n: 4, title: "Engagement contractuel", body: "La formule Location est souscrite pour une durée minimale de 12 mois calendaires à compter de la mise en ligne du site.\n\nCet engagement de 12 mois garantit une prestation pérenne et permet la mise en place de la formule Location sans investissement initial de 3 000 €. Il couvre l'ensemble de la période contractuelle (création, hébergement, nom de domaine, maintenance et support inclus)." },
  { n: 5, title: "Résiliation", body: "Aucune résiliation anticipée n'est possible pendant la période d'engagement minimum de 12 mois, sauf cas de force majeure ou manquement grave d'EM Group dûment constaté.\n\nÀ l'issue de cette période minimum de 12 mois, le contrat se poursuit tacitement au tarif mensuel convenu et devient résiliable à tout moment par le client avec un préavis de 30 jours calendaires, notifié par email à contact@emgroupdigital.fr.\n\nEn cas de résiliation anticipée à l'initiative du client hors des cas prévus ci-dessus, l'intégralité des mensualités restantes jusqu'à la fin de la période d'engagement reste due." },
  { n: 6, title: "Propriété du site", body: "Dans le cadre de la formule Location, EM Group demeure propriétaire du site (design, code source, développements techniques) pendant toute la durée du contrat. Le client bénéficie d'un droit d'usage exclusif du site dans le cadre de son activité professionnelle.\n\nLe client conserve la propriété pleine et entière de sa marque, de son logo et des contenus qu'il fournit (textes, photos, vidéos).\n\nUn rachat du site en fin de contrat est possible sur devis." },
  { n: 7, title: "Contenu et validation", body: "Le client fournit à EM Group les éléments nécessaires à la création du site (textes, photos, logo, coordonnées, mentions légales). En l'absence de photos fournies, EM Group utilise des visuels libres de droits.\n\nLe site est soumis à validation du client avant mise en ligne. Le client dispose de 7 jours ouvrés pour valider ou demander des modifications. Passé ce délai sans retour, le site est considéré validé et mis en ligne." },
  { n: 8, title: "Maintenance et disponibilité", body: "EM Group assure la maintenance technique du site (mises à jour de sécurité, corrections de bugs, sauvegardes régulières) pendant toute la durée du contrat.\n\nEM Group s'engage à un taux de disponibilité du site de 99% en moyenne annuelle, hors interventions planifiées et cas de force majeure. Les interruptions techniques prolongées sont notifiées au client dans les meilleurs délais." },
  { n: 9, title: "Modifications du site", body: "Les modifications mineures (mise à jour de textes, ajout d'images, changement de coordonnées) sont incluses dans la formule Location dans la limite de 2 demandes par mois.\n\nLes modifications majeures (nouvelle page, refonte graphique, ajout de fonctionnalités, module e-commerce) font l'objet d'un devis séparé et acceptation préalable du client." },
  { n: 10, title: "Nom de domaine et hébergement", body: "Le nom de domaine est enregistré au nom du client, avec EM Group comme prestataire technique. En cas de résiliation du contrat, le client peut demander le transfert du nom de domaine à ses frais.\n\nL'hébergement est fourni par EM Group sur des serveurs professionnels sécurisés situés en Europe. Les données du site sont sauvegardées quotidiennement." },
  { n: 11, title: "Paiement", body: "Le paiement s'effectue mensuellement par prélèvement SEPA ou virement bancaire, selon accord entre les parties.\n\nEn cas de retard de paiement supérieur à 15 jours après relance, EM Group se réserve le droit de suspendre l'accès au site jusqu'à régularisation. Des pénalités de retard au taux légal en vigueur pourront être appliquées conformément à la législation française." },
  { n: 12, title: "Droit applicable", body: "Les présentes CGV sont soumises au droit français. En cas de litige, les parties s'engagent à rechercher une solution amiable avant tout recours judiciaire.\n\nÀ défaut d'accord amiable, le tribunal compétent sera celui du siège social d'EM Group Digital." },
];

export default function SiteLocation() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", has_website: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [openCgv, setOpenCgv] = useState(-1);

  useEffect(() => {
    // SEO géré par <SEOHead />. On reste sur ce useEffect pour des effets éventuels.
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim() || !form.has_website) {
      setError("Merci de remplir tous les champs.");
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams(window.location.search);
      await axios.post(`${API}/leads/site-location`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        has_website: form.has_website,
        utm_source: params.get("utm_source") || "",
        utm_campaign: params.get("utm_campaign") || "",
      });
      fireLeadConversion(30);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue. Réessayez ou écrivez-nous à contact@emgroupdigital.fr");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0E] text-white relative overflow-hidden" data-testid="site-location-page">
      <SEOHead
        title={TITLE}
        description={DESCRIPTION}
        keywords={KEYWORDS}
        canonical={CANONICAL}
        ogImage="https://emgroupdigital.fr/brand/logo-em-group.png"
        locale="fr_FR"
        htmlLang="fr"
        robots="index, follow"
        localBusiness={LOCAL_BUSINESS}
        serviceSchema={SERVICE_SCHEMA}
        faqSchema={FAQ_ITEMS}
      />
      {/* Ambient gradient */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-[#E52431]/20 blur-[120px]" />
        <div className="absolute top-1/3 -right-40 w-[420px] h-[420px] rounded-full bg-[#FF6A75]/10 blur-[120px]" />
      </div>

      {/* Header — logo only, no nav */}
      <header className="relative z-10 px-6 md:px-10 pt-6 md:pt-8">
        <Link to="/" data-testid="site-location-logo" className="inline-flex items-center gap-2 group">
          <img
            src="/brand/logo-em-group.png"
            alt="EM Group — Agence digitale et création de site web à La Réunion"
            width="40"
            height="40"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="w-10 h-10 rounded-full"
          />
          <span className="font-outfit text-base font-medium tracking-tight text-white/90 group-hover:text-white transition">
            EM Group
          </span>
        </Link>
      </header>

      {/* Main */}
      <main className="relative z-10 px-6 md:px-10 py-10 md:py-16 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-start">
          {/* LEFT — Pitch */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-[0.15em] text-white/65 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E52431] animate-pulse" />
              Offre Loueur — TPE / PME
            </div>

            <h1 className="mt-6 font-outfit text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
              Création de{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E52431] to-[#FF6A75] font-medium">
                site web à La Réunion
              </span>
              <br className="hidden md:block" />
              <span className="text-white/85">à partir de 180€/mois.</span>
            </h1>

            <p className="mt-5 text-white/65 text-base md:text-lg leading-relaxed max-w-xl">
              Agence digitale basée à La Réunion. Création complète, hébergement, nom de domaine, maintenance : <strong className="text-white">tout est inclus</strong>. Site professionnel livré en 15 à 30 jours, sans investir 3 000 € au départ.
            </p>

            {/* Arguments */}
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {ARGUMENTS.map(({ Icon, title, text }, i) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.15 + i * 0.08 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.025] p-5 hover:bg-white/[0.04] hover:border-white/20 transition"
                  data-testid={`site-location-arg-${i}`}
                >
                  <div className="w-9 h-9 rounded-xl bg-[#E52431]/15 border border-[#E52431]/30 flex items-center justify-center mb-3">
                    <Icon size={16} className="text-[#FFB4BB]" />
                  </div>
                  <div className="font-medium text-white text-[15px]">{title}</div>
                  <div className="text-sm text-white/55 leading-relaxed mt-1">{text}</div>
                </motion.div>
              ))}
            </div>

            <div className="mt-8 hidden lg:flex items-center gap-3 text-xs text-white/45">
              <ShieldCheck size={13} />
              <span>Engagement clair, conditions transparentes. Pas de surprise.</span>
            </div>
          </motion.div>

          {/* RIGHT — Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:sticky lg:top-8"
          >
            {!submitted ? (
              <form
                onSubmit={onSubmit}
                className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-white/[0.02] p-6 md:p-7 backdrop-blur-xl shadow-2xl shadow-black/40"
                data-testid="site-location-form"
              >
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#E52431] font-bold mb-1">— Demande de devis</div>
                <h2 className="font-outfit text-2xl font-light tracking-tight">Discutons de votre projet.</h2>
                <p className="text-xs text-white/50 mt-1">Réponse personnalisée sous 24h. Aucun engagement.</p>

                <div className="mt-6 space-y-4">
                  <div>
                    <label className="text-xs text-white/55 font-medium uppercase tracking-[0.1em]" htmlFor="sl-name">Nom complet</label>
                    <input
                      id="sl-name"
                      type="text"
                      autoComplete="name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="site-location-name"
                      className="mt-1.5 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#E52431]/60 focus:bg-black/40 outline-none transition"
                      placeholder="Jean Dupont"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-white/55 font-medium uppercase tracking-[0.1em]" htmlFor="sl-phone">Téléphone</label>
                      <input
                        id="sl-phone"
                        type="tel"
                        autoComplete="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        data-testid="site-location-phone"
                        className="mt-1.5 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#E52431]/60 focus:bg-black/40 outline-none transition"
                        placeholder="06 92 …"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs text-white/55 font-medium uppercase tracking-[0.1em]" htmlFor="sl-email">Email</label>
                      <input
                        id="sl-email"
                        type="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="site-location-email"
                        className="mt-1.5 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#E52431]/60 focus:bg-black/40 outline-none transition"
                        placeholder="vous@entreprise.fr"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-white/55 font-medium uppercase tracking-[0.1em] mb-2">Avez-vous déjà un site web ?</div>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { value: "yes", label: "Oui" },
                        { value: "no", label: "Non" },
                      ].map((opt) => {
                        const active = form.has_website === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setForm({ ...form, has_website: opt.value })}
                            data-testid={`site-location-has-website-${opt.value}`}
                            className={`px-4 py-3 rounded-xl text-sm font-medium transition border ${
                              active
                                ? "bg-[#E52431]/15 border-[#E52431]/50 text-white"
                                : "bg-black/30 border-white/10 text-white/65 hover:bg-black/40 hover:text-white"
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="mt-4 p-3 rounded-xl bg-[#E52431]/10 border border-[#E52431]/30 text-sm text-[#FFB4BB]" data-testid="site-location-error">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="site-location-submit"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#E52431] to-[#FF6A75] text-white font-semibold px-6 py-4 text-sm uppercase tracking-[0.12em] hover:shadow-[0_0_40px_rgba(229,36,49,0.45)] transition disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                  {loading ? "Envoi en cours…" : "Demander mon devis gratuit"}
                </button>

                <div className="mt-4 flex items-start gap-2 text-[11px] text-white/40 leading-relaxed">
                  <ShieldCheck size={11} className="mt-0.5 shrink-0" />
                  <span>Vos coordonnées restent confidentielles. Pas de spam. Vous pouvez demander leur suppression à tout moment.</span>
                </div>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="rounded-3xl border border-emerald-500/30 bg-emerald-500/[0.05] p-8 text-center backdrop-blur-xl"
                data-testid="site-location-success"
              >
                <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/40 mx-auto flex items-center justify-center">
                  <Check size={28} className="text-emerald-300" />
                </div>
                <h2 className="mt-5 font-outfit text-2xl font-light tracking-tight">Demande bien reçue.</h2>
                <p className="mt-2 text-white/65 text-sm leading-relaxed">
                  On vous rappelle <strong className="text-white">sous 24h</strong> pour cadrer votre projet. <br />
                  Vous pouvez aussi nous écrire à <a href="mailto:contact@emgroupdigital.fr" className="text-[#FFB4BB] underline">contact@emgroupdigital.fr</a>.
                </p>
                <Link to="/" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-white/60 hover:text-white transition">
                  Découvrir EM Group <ArrowRight size={12} />
                </Link>
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Social proof — clients & testimonials */}
        <section
          className="mt-20 md:mt-28 max-w-5xl mx-auto"
          aria-labelledby="proof-title"
          data-testid="site-location-social-proof"
        >
          <div className="text-center mb-10 md:mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] text-[10px] uppercase tracking-[0.18em] text-white/55 font-semibold">
              <Star size={11} className="fill-amber-400 text-amber-400" />
              Ils nous font confiance
            </div>
            <h2 id="proof-title" className="mt-5 font-outfit text-3xl md:text-4xl font-light tracking-tight">
              Des entreprises de La Réunion <br className="hidden sm:block" />
              <span className="text-white/55">qui en parlent mieux que nous.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <TestimonialCard key={t.company} {...t} index={i} />
            ))}
          </div>
        </section>

        {/* FAQ structurée */}
        <FAQSection
          items={FAQ_ITEMS}
          testIdPrefix="site-location-faq"
          title="Questions fréquentes sur la création de site web à La Réunion."
        />
      </main>

      {/* CGV — Conditions Générales de Vente */}
      <section className="relative z-10 bg-[#06060A] border-t border-white/5 px-6 md:px-10 py-14" data-testid="cgv-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold">— Cadre légal</div>
          <h2 className="mt-2 font-outfit text-2xl md:text-3xl font-light tracking-tight">
            Conditions Générales de Vente — Formule Location La Réunion
          </h2>
          <p className="mt-3 text-sm text-white/55 leading-relaxed">
            Engagement 12 mois minimum d&apos;abonnement à compter de la mise en ligne. Résiliation avec préavis de 30 jours à l&apos;issue de la période minimum.
          </p>
          <div className="mt-6 rounded-3xl border border-white/8 bg-white/[0.015] divide-y divide-white/5 overflow-hidden">
            {CGV_ARTICLES.map((art, i) => {
              const isOpen = openCgv === i;
              return (
                <div key={art.n} data-testid={`cgv-article-${art.n}`}>
                  <button
                    type="button"
                    onClick={() => setOpenCgv(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    data-testid={`cgv-article-toggle-${art.n}`}
                    className="w-full text-left flex items-start gap-4 px-5 md:px-7 py-4 hover:bg-white/[0.015] transition"
                  >
                    <span className="shrink-0 mt-0.5 text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono">
                      Art. {art.n}
                    </span>
                    <span className="flex-1 text-sm md:text-base font-medium text-white/85">{art.title}</span>
                    <ChevronDown
                      size={14}
                      className={`shrink-0 mt-1 text-white/45 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-5 md:px-7 pb-5 text-xs md:text-sm text-white/65 leading-relaxed whitespace-pre-line">
                      {art.body}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Minimal footer */}
      <footer className="relative z-10 px-6 md:px-10 py-8 mt-10 text-[11px] text-white/35 text-center">
        © {new Date().getFullYear()} EM Group · <a href="/mentions-legales" className="hover:text-white/60 transition">Mentions légales</a> · <a href="/confidentialite" className="hover:text-white/60 transition">Confidentialité</a>
      </footer>
    </div>
  );
}
